import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Popover, TextField, InputAdornment, List, ListItem, ListItemText,
    CircularProgress, IconButton, Tooltip, Tabs, Tab, Button,
    Select, MenuItem
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { searchPhoneContacts } from '@/service/phoneContactService';
import { normalizeString } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';
import Utils from '@/common/utils/utils';
import PhoneCallIcon from '@/component/telephoneAgent/TelephoneIcon/PhoneCallIcon';
import VoiceCallIcon from '@/component/telephoneAgent/TelephoneIcon/VoiceCallIcon';
import './PhoneSearchPopoverV2.css';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom, projectListInCorporationByUserIdAtom } from '@/state/atom/projectAtom';
import { voiceStatusAtom, outboundCallAtom } from '@/state/atom/voiceCallAtom';
import { appIntegrationSessionAtom, appIntegrationSilenceAtom } from '@/state/atom/appIntegrationAtom';
import { handleAction as twilioHandleAction } from '@/service/twilioService';
import { getB2CUserListInProject } from '../chatThread/handler/TwilioHandler';
import { setupConnectionHandlers } from '@/hooks/useOutboundCall';
import useAppIntegrationAudioDevices from '@/hooks/useAppIntegrationAudioDevices';
import * as appIntegrationService from '@/service/appIntegrationService';
import { useSnackbar } from 'notistack';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import tr from '@/common/utils/tr';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

const { PHONE_SEARCH_TAB, PHONE_SEARCH_PAGE_SIZE, RECIPIENT_TYPE } = TelephoneConstant;
const ENABLE_APP_INTEGRATION_FAKE_START = process.env.REACT_APP_ENABLE_APP_INTEGRATION_FAKE_START === 'true';

/**
 * OverflowTooltip - Only shows tooltip when text is truncated
 */
function OverflowTooltip({ title, children, ...tooltipProps }) {
    const [isOverflowed, setIsOverflowed] = React.useState(false);
    const ref = React.useRef(null);

    const checkOverflow = React.useCallback(() => {
        const el = ref.current;
        if (el) {
            const textEl = el.querySelector('.MuiListItemText-primary')
                || el.querySelector('.MuiButton-label')
                || el;
            setIsOverflowed(textEl.scrollWidth > textEl.clientWidth);
        }
    }, []);

    React.useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow, title]);

    return (
        <Tooltip title={isOverflowed ? title : ''} placement="right" {...tooltipProps}>
            {React.cloneElement(children, { ref })}
        </Tooltip>
    );
}

const parseAppIntegrationStartTime = (value, fallback) => {
    if (!value) return fallback;
    if (typeof value === 'number') return value;
    const parsedTime = new Date(value).getTime();
    return Number.isFinite(parsedTime) ? parsedTime : fallback;
};

const normalizeAppIntegrationStartData = (resultData, fallbackSessionId, fallbackStartTime) => {
    const data = resultData?.data || {};
    const ticketData = data.ticketData || {};
    const chatThreadData = data.chatThreadData || ticketData.chatThreadData || {};

    return {
        sessionId: data.sessionId || ticketData.sessionId || fallbackSessionId,
        ticketId: data.ticketId || ticketData.ticketId || ticketData.id || null,
        chatThreadId: data.chatThreadId ||
            data.customerChatThreadId ||
            ticketData.customerChatThreadId ||
            chatThreadData.customerChatThreadId ||
            chatThreadData.id ||
            null,
        chatThreadData,
        startTime: parseAppIntegrationStartTime(data.startTime || ticketData.startTime, fallbackStartTime),
        openAIKeyMetadata: data.openAIKeyMetadata || null,
    };
};

const buildFakeAppIntegrationStartData = (sessionId, startTime) => ({
    sessionId,
    ticketId: `fake_ticket_${sessionId}`,
    chatThreadId: `fake_chat_thread_${sessionId}`,
    chatThreadData: {
        id: `fake_chat_thread_${sessionId}`,
        customerChatThreadId: `fake_chat_thread_${sessionId}`,
    },
    startTime,
    isFake: true,
});

/**
 * PhoneSearchPopoverV2 Component
 * Tabbed popover for searching phone contacts, recent calls, and AI training
 *
 * @param {Object} props
 * @param {boolean} props.open - Popover open state
 * @param {Element} props.anchorEl - Anchor element for popover
 * @param {Function} props.onClose - Close popover handler
 * @param {Function} props.onSelectPhone - Phone selection handler
 * @param {Function} props.onMakeCallAiTraining - AI training call handler
 * @param {String} props.projectId - Current project ID
 * @param {String} props.corporationId - Current corporation ID
 * @param {String} props.userId - Current user ID
 */
function PhoneSearchPopoverV2(props) {
    const {
        open,
        anchorEl,
        onClose,
        onSelectPhone,
        onMakeCallAiTraining
    } = props;

    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    // common userDetail state
    const userDetail = useRecoilValue(userAtom);
    const userId = userDetail?.id;

    // common corporationDetail state
    const corporationDetail = useRecoilValue(corporationAtom);
    const corporationId = corporationDetail?.id;
    
    // common projectInuse state
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const projectId = projectInuseDetail?.id;

    const currentListProjects = useRecoilValue(projectListInCorporationByUserIdAtom);

    // @voice call state
    const voiceStatus = useRecoilValue(voiceStatusAtom);
    const appIntegrationSession = useRecoilValue(appIntegrationSessionAtom);
    const setVoiceStatus = useSetRecoilState(voiceStatusAtom);
    const setOutboundCall = useSetRecoilState(outboundCallAtom);
    const setAppIntegrationSession = useSetRecoilState(appIntegrationSessionAtom);
    const setAppIntegrationSilence = useSetRecoilState(appIntegrationSilenceAtom);
    const { enqueueSnackbar } = useSnackbar();

    const [listB2CUserListInProject, setListB2CUserListInProject] = useState([]);
    
    const isShowTabContactAndHistory = useMemo(() => {
        return Utils.isEnableSettingTelephoneAgent(corporationDetail);
    }, [corporationDetail]);

    // AI Training only depends on the corporation telephone setting.
    // The project telephone setting must NOT be checked here: training calls run over
    // OpenAI WebRTC (browser mic), not over the project telephone line.
    // Guest contracts are excluded to stay aligned with the backend permission check.
    const isShowTabTraining = useMemo(() => {
        const isGuestContract = [
            Constant.CORPORATION_CONTRACT_TYPE.GUEST,
            Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
        ].includes(corporationDetail?.contractType);

        return isShowTabContactAndHistory && projectInuseDetail?.isDesk && !isGuestContract;
    }, [isShowTabContactAndHistory, corporationDetail?.contractType, projectInuseDetail?.isDesk]);

    const isShowTabAppIntegration = useMemo(() => {
        return isShowTabContactAndHistory && projectInuseDetail?.isDesk;
    }, [isShowTabContactAndHistory, projectInuseDetail?.isDesk]);

    const [activeTab, setActiveTab] = useState(PHONE_SEARCH_TAB.HISTORY);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [recipientType, setRecipientType] = useState(RECIPIENT_TYPE.PROJECT_IN_ORG);
    const recipientTypeRef = useRef(RECIPIENT_TYPE.PROJECT_IN_ORG);

    // Track active tab to prevent stale responses from overwriting current tab data
    const activeTabRef = useRef(PHONE_SEARCH_TAB.HISTORY);

    // Data states
    const [tabData, setTabData] = useState([]);
    const [isLoadingTabData, setIsLoadingTabData] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Search results
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Pagination for backend pagination (TAB_TRAINING & TAB_CONTACTS)
    const currentPageRef = useRef(0);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMoreTraining, setHasMoreTraining] = useState(false);
    const [isStartingAppIntegration, setIsStartingAppIntegration] = useState(false);

    // Pagination: visible count per tab (frontend pagination for HISTORY/CONTACTS)
    const [visibleHistoryCount, setVisibleHistoryCount] = useState(PHONE_SEARCH_PAGE_SIZE);
    const [visibleContactCount, setVisibleContactCount] = useState(PHONE_SEARCH_PAGE_SIZE);

    const {
        appIntegrationDevices,
        isLoadingAppIntegrationDevices,
        isAppIntegrationMicPermissionGranted,
        appIntegrationDeviceError,
        appIntegrationSelectionError,
        customerAudioDeviceId,
        userAudioDeviceId,
        clearAppIntegrationSelectionError,
        saveAppIntegrationDevices,
        setCustomerAudioDeviceId,
        updateAppIntegrationSelectionError,
        setUserAudioDeviceId,
        loadAppIntegrationAudioDevices,
        stopAppIntegrationMediaStream,
        resetAppIntegrationAudioDeviceSelection,
        resetAppIntegrationLoadingState,
    } = useAppIntegrationAudioDevices({
        corporationId,
        isDeviceChangeListenerActive: open && activeTab === PHONE_SEARCH_TAB.APP_INTEGRATION,
        projectId,
        userId,
    });

    // Tab order for MUI Tabs index mapping (dynamic based on visible tabs)
    const TAB_ORDER = useMemo(() => {
        const tabs = [];
        if (isShowTabContactAndHistory) {
            tabs.push(PHONE_SEARCH_TAB.HISTORY, PHONE_SEARCH_TAB.CONTACTS);
        }
        if (isShowTabTraining) {
            tabs.push(PHONE_SEARCH_TAB.TRAINING);
        }
        if (isShowTabAppIntegration) {
            tabs.push(PHONE_SEARCH_TAB.APP_INTEGRATION);
        }
        return tabs;
    }, [isShowTabContactAndHistory, isShowTabTraining, isShowTabAppIntegration]);
    const tabIndex = TAB_ORDER.indexOf(activeTab);

    /**
     * Reset all search/pagination state
     */
    const resetSearchState = useCallback((isResetSearchKeyword = true) => {
        if (isResetSearchKeyword) {
            setSearchKeyword('');
        }
        setSearchResults([]);
        setHasSearched(false);
        setIsSearching(false);
        setTabData([]);
        setVisibleHistoryCount(PHONE_SEARCH_PAGE_SIZE);
        setVisibleContactCount(PHONE_SEARCH_PAGE_SIZE);
        currentPageRef.current = 0;
        setTotalCount(0);
        setHasMoreTraining(false);
    }, []);

    /**
     * Load data for current tab
     */
    const loadTabData = useCallback(async (tab, keyword = '') => {
        if (!projectId || !corporationId) {
            setTabData([]);
            return;
        }
        setIsLoadingTabData(true);
        try {
            const normalizedKeyword = keyword ? normalizeString(keyword) : '';
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: tab, recipientType: recipientTypeRef.current
            });
            // Discard stale response if user already switched to a different tab
            if (activeTabRef.current !== tab) return;
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                setTabData([]);
                return;
            }
            const data = results.data;
            // All tabs return unified format: { items, totalCount, hasMore }
            setTabData(data?.items || []);
            setTotalCount(data?.totalCount || 0);
            setHasMoreTraining(data?.hasMore || false);
            currentPageRef.current = 0;
        } catch (error) {
            console.log('loadTabData >>> error >>>', error)
            // Only clear data if this tab is still active
            if (activeTabRef.current === tab) {
                setTabData([]);
            }
        } finally {
            if (activeTabRef.current === tab) {
                setIsLoadingTabData(false);
            }
        }
    }, [projectId, corporationId, userId]);

    /**
     * Load more data (page-based pagination for TAB_TRAINING & TAB_CONTACTS)
     */
    const loadMorePaginated = useCallback(async () => {
        if (!hasMoreTraining) return;
        if (!projectId || !corporationId) return;
        setIsLoadingMore(true);
        try {
            const nextPage = currentPageRef.current + 1;
            const normalizedKeyword = searchKeyword ? normalizeString(searchKeyword) : '';
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: activeTab,
                page: nextPage, recipientType: recipientTypeRef.current
            });
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                return;
            }
            const data = results.data;
            if (data?.items) {
                if (data.items.length === 0) {
                    setHasMoreTraining(false);
                    return;
                }
                setTabData(prev => [...prev, ...data.items]);
                setHasMoreTraining(data.hasMore || false);
                currentPageRef.current = nextPage;
            }
        } catch (error) {
            console.log('loadMorePaginated error >>>', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [userId, projectId, corporationId, searchKeyword, hasMoreTraining, activeTab]);

    // Reset state when popover opens/closes
    useEffect(() => {
        if (open) {
            resetSearchState();
            const defaultTab = TAB_ORDER[0] || PHONE_SEARCH_TAB.HISTORY;
            setActiveTab(defaultTab);
            activeTabRef.current = defaultTab;
            recipientTypeRef.current = RECIPIENT_TYPE.PROJECT_IN_ORG;
            setRecipientType(RECIPIENT_TYPE.PROJECT_IN_ORG);

            (async () => {
                try {
                    await Promise.all([
                        // App Integration tab does not need to load phone contact data
                        defaultTab !== PHONE_SEARCH_TAB.APP_INTEGRATION
                            ? loadTabData(defaultTab)
                            : loadAppIntegrationAudioDevices(),
                        (async () => {
                            const data = await getB2CUserListInProject(corporationDetail?.id, projectInuseDetail?.id);
                            setListB2CUserListInProject(data);
                        })()
                    ]);
                } catch (err) {
                    console.error('loadTab when open error >>>', err);
                }
            })();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload data when recipientType changes (TAB_CONTACTS only)
    useEffect(() => {
        if (open && activeTab === PHONE_SEARCH_TAB.CONTACTS) {
            loadTabData(PHONE_SEARCH_TAB.CONTACTS);
        }
    }, [recipientType]); // eslint-disable-line react-hooks/exhaustive-deps



    /**
     * Perform search
     */
    const performSearch = async () => {
        if (!projectId || !corporationId) {
            setSearchResults([]);
            return;
        }

        // Reset pagination state before new search
        setSearchResults([]);
        setHasMoreTraining(false);
        currentPageRef.current = 0;
        setVisibleHistoryCount(PHONE_SEARCH_PAGE_SIZE);
        setVisibleContactCount(PHONE_SEARCH_PAGE_SIZE);

        // TAB_TRAINING & TAB_CONTACTS: use loadTabData (same data flow, supports Load More)
        if (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS) {
            setHasSearched(false);
            loadTabData(activeTab, searchKeyword);
            return;
        }

        // TAB_HISTORY: use searchResults
        setIsSearching(true);
        setHasSearched(true);
        try {
            const normalizedKeyword = normalizeString(searchKeyword);
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: activeTab, recipientType: recipientTypeRef.current
            });
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                setSearchResults([]);
                return;
            }
            const data = results.data;
            setSearchResults(data?.items || []);
        } catch (error) {
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            performSearch();
        }
    };

    const handleSelectPhone = (phoneData) => {
        if (onSelectPhone) onSelectPhone(phoneData);
        handleClose();
    };

    const handleMakeCallAiTraining = (aiOperatorTicket) => {
        if (!aiOperatorTicket) return;
        if (onMakeCallAiTraining) onMakeCallAiTraining(aiOperatorTicket);
        handleClose();
    };

    /**
     * Find B2C user from listB2CUserListInProject by matching phone number
     */
    const findB2CUserByPhone = useCallback((phoneKeyword) => {
        if (!phoneKeyword || !listB2CUserListInProject?.length) return null;
        const cleanedKeyword = Utils.cleanPhone(phoneKeyword);
        if (!cleanedKeyword) return null;
        return listB2CUserListInProject.find((user) => {
            const cleanedTel = Utils.cleanPhone(user?.tel);
            return cleanedTel && cleanedTel === cleanedKeyword;
        }) || null;
    }, [listB2CUserListInProject]);

    const handleDirectCall = () => {
        const cleanedPhone = Utils.cleanPhone(searchKeyword);
        const matchedB2CUser = findB2CUserByPhone(searchKeyword);
        handleSelectPhone({
            phone: cleanedPhone,
            name: matchedB2CUser?.displayName || cleanedPhone,
            type: 'direct',
            userId: matchedB2CUser?.id || null
        });
    };

    const handleVoiceCall = async (phoneData) => {
        if (!phoneData?.projectId && !phoneData?.agentIdentity) return;

        if (!window.twilioDevice) {
            console.error('handleVoiceCall: Twilio device not initialized');
            return;
        }

        try {
            let requestBody = {
                tabType: activeTab,
                recipientType: recipientType,
            }

            if (phoneData.userId) {
                const baseProjectId = currentListProjects?.find(p => p.type === Constant.PROJECT_TYPE.BASE)?.id;
                requestBody = { ...requestBody, calleeUserId: phoneData.userId, baseProjectId: baseProjectId };
            } else if (phoneData.projectId) {
                requestBody = { ...requestBody, calleeProjectId: phoneData.projectId };
            }

            const result = await twilioHandleAction({
                actionType: TelephoneConstant.ACTION.GET_AVAILABLE_AGENTS_FOR_VOICE_CALL,
                corporationInuse: corporationId,
                projectInuse: projectId,
                userId: userId,
                requestBody: JSON.stringify(requestBody),
            });

            if (!result || !result.status) {
                console.error('handleVoiceCall: debug response >>> ', result);
                setShowErrorModal(true);
                return;
            }
            
            // Handle error codes from backend
            if (result?.errorCode) {
                const errorCode = result.errorCode;
                
                // Show Popup 030 for DATA_CHANGED_PLEASE_RELOAD_ERROR_MSG_CODE
                if (errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR_MSG_CODE) {
                    setShowErrorModal(true);
                    return;
                }
                
                // Show toast notification for other error codes
                const errorMessageKeys = [
                    'TEL_INFO_NO_AVAILABLE_RECEIVER',
                    'TEL_INFO_CALL_ALREADY_IN_PROGRESS',
                    'TEL_ERR_TARGET_DELETED',
                    'INTERNAL_ERROR'
                ];

                // A known error code is also its locale key. Anything else falls back to
                // the server text, then to the generic key.
                let errorMessage;
                if (errorMessageKeys.includes(errorCode)) {
                    errorMessage = buildSnackbarMessage(errorCode);
                } else {
                    errorMessage = result?.message || buildSnackbarMessage('VOICE_CALL_ERROR');
                }
                let variant = 'error';
                if (
                    errorCode === 'TEL_INFO_NO_AVAILABLE_RECEIVER' ||
                    errorCode === 'TEL_INFO_CALL_ALREADY_IN_PROGRESS'
                ) {
                    variant = 'info';
                }
                enqueueSnackbar(errorMessage, { variant: variant, autoHideDuration: 3000 });
                return;
            }

            const agents = result.data?.agents || [];
            if (agents.length === 0) {
                enqueueSnackbar(buildSnackbarMessage('TEL_INFO_NO_AVAILABLE_RECEIVER'), { variant: 'info', autoHideDuration: 3000 });
                return;
            }

            const calleeProjectId = phoneData.projectId || agents[0]?.projectId || '';
            const agentIdentities = agents.map(a => a.identity).join(',');

            const connection = await window?.twilioDevice?.connect({
                params: {
                    To: agentIdentities,
                    callType: TelephoneConstant.CALL_TYPE.VOICE_CALL,
                    callerProjectId: projectId,
                    calleeProjectId: calleeProjectId,
                }
            });
            if (!connection) {
                enqueueSnackbar(buildSnackbarMessage('VOICE_CALL_ERROR'), { variant: 'error', autoHideDuration: 3000 });
                return;
            }
            window.twilioOutboundConnection = connection;

            // Set outboundCall of state by phoneData
            let outboundDataState = {
                phoneNumber: '',
                customerName: phoneData?.name || '',
                callSid: connection?.parameters?.CallSid || '',
                timestamp: Date.now(),
            };
            const { USER, PROJECT } = Constant.TELEPHONE_SETTINGS.CALL_TYPE; // available target
            const callType = phoneData.callType;
            if (callType === USER) {
                outboundDataState.phoneNumber = phoneData?.userId;
                outboundDataState.image = agents[0]?.image || '/assets/image/icon-user-default.svg';
            } else if (callType === PROJECT) {
                outboundDataState.phoneNumber = phoneData?.projectId;
                outboundDataState.image = '';
            }
            setOutboundCall(outboundDataState);

            setVoiceStatus(Constant.PHONE_AVAILABILITY.CONNECTING);

            setupConnectionHandlers(connection, setOutboundCall, setVoiceStatus, enqueueSnackbar);

            handleClose();
        } catch (error) {
            console.error('handleVoiceCall error:', error);
            enqueueSnackbar(buildSnackbarMessage('VOICE_CALL_ERROR'), { variant: 'error', autoHideDuration: 3000 });
        }
    };

    const handleClose = useCallback(() => {
        resetSearchState();
        stopAppIntegrationMediaStream();
        resetAppIntegrationAudioDeviceSelection();
        resetAppIntegrationLoadingState();
        if (onClose) onClose();
    }, [
        onClose,
        resetAppIntegrationAudioDeviceSelection,
        resetAppIntegrationLoadingState,
        resetSearchState,
        stopAppIntegrationMediaStream,
    ]);

    const handleTabChange = (event, newValue) => {
        const newTab = TAB_ORDER[newValue];
        setActiveTab(newTab);
        activeTabRef.current = newTab;

        // reset input search (https://new.redmine.ominext.com/issues/296199)
        const isResetSearchKeyword = true;
        resetSearchState(isResetSearchKeyword);

        // App Integration tab does not load phone contact data
        if (newTab !== PHONE_SEARCH_TAB.APP_INTEGRATION) {
            resetAppIntegrationAudioDeviceSelection();
            loadTabData(newTab);
        } else {
            loadAppIntegrationAudioDevices();
        }
    };

    const handleRecipientTypeChange = (event) => {
        const newRecipientType = event.target.value;
        recipientTypeRef.current = newRecipientType;
        setRecipientType(newRecipientType);

        // Reset and reload with new recipient type
        const isResetSearchKeyword = false;
        resetSearchState(isResetSearchKeyword);
    };

    const handleChangeCustomerAudioDevice = useCallback((deviceId) => {
        setCustomerAudioDeviceId(deviceId);
        if (deviceId && userAudioDeviceId && deviceId === userAudioDeviceId) {
            updateAppIntegrationSelectionError('APP_INTEGRATION_SAME_DEVICE_ERROR');
            return;
        }
        if (deviceId && userAudioDeviceId) {
            clearAppIntegrationSelectionError();
        }
    }, [
        clearAppIntegrationSelectionError,
        updateAppIntegrationSelectionError,
        setCustomerAudioDeviceId,
        userAudioDeviceId,
    ]);

    const handleChangeUserAudioDevice = useCallback((deviceId) => {
        setUserAudioDeviceId(deviceId);
        if (customerAudioDeviceId && deviceId && customerAudioDeviceId === deviceId) {
            updateAppIntegrationSelectionError('APP_INTEGRATION_SAME_DEVICE_ERROR');
            return;
        }
        if (customerAudioDeviceId && deviceId) {
            clearAppIntegrationSelectionError();
        }
    }, [
        clearAppIntegrationSelectionError,
        customerAudioDeviceId,
        updateAppIntegrationSelectionError,
        setUserAudioDeviceId,
    ]);

    const handleStartAppIntegration = useCallback(async () => {
        if (!customerAudioDeviceId || !userAudioDeviceId) return;

        if (customerAudioDeviceId === userAudioDeviceId) {
            updateAppIntegrationSelectionError('APP_INTEGRATION_SAME_DEVICE_ERROR');
            return;
        }

        if (!corporationId || !projectId || !userId || isStartingAppIntegration) return;

        if (appIntegrationSession ||
            [Constant.PHONE_AVAILABILITY.CONNECTING, Constant.PHONE_AVAILABILITY.ACTIVE].includes(voiceStatus)) {
            updateAppIntegrationSelectionError('INTERNAL_ERROR');
            return;
        }

        const sessionId = `app_integration_${projectId}_${userId}_${Date.now()}`;
        const startTime = Date.now();
        const previousVoiceStatus = voiceStatus;

        saveAppIntegrationDevices({ customerAudioDeviceId, userAudioDeviceId });
        clearAppIntegrationSelectionError();
        setIsStartingAppIntegration(true);
        setVoiceStatus(Constant.PHONE_AVAILABILITY.CONNECTING);

        const appIntegrationSessionContext = {
            corpId: corporationId,
            projectId,
            userId,
            customerDeviceId: customerAudioDeviceId,
            agentDeviceId: userAudioDeviceId,
            language: projectInuseDetail?.language || corporationDetail?.language || 'ja',
        };

        const openPendingAppIntegrationSession = () => {
            setAppIntegrationSilence({
                isSilent: false,
                silenceStartedAt: null,
                type: null,
            });
            setAppIntegrationSession({
                sessionId,
                ticketId: null,
                chatThreadId: null,
                chatThreadData: null,
                startTime,
                ...appIntegrationSessionContext,
                status: 'starting',
                endReason: null,
            });
            handleClose();
        };

        const completeAppIntegrationSession = (sessionData) => {
            setAppIntegrationSession((currentSession) => {
                if (!currentSession || currentSession.sessionId !== sessionId) {
                    return currentSession;
                }

                return {
                    ...currentSession,
                    ...sessionData,
                    ...appIntegrationSessionContext,
                    status: 'active',
                };
            });
            setVoiceStatus(Constant.PHONE_AVAILABILITY.ACTIVE);
        };

        try {
            openPendingAppIntegrationSession();

            const resultData = await appIntegrationService.startAppIntegrationCall(
                corporationId,
                projectId,
                userId,
                {
                    sessionId,
                    startTime,
                    customerDeviceId: customerAudioDeviceId,
                    agentDeviceId: userAudioDeviceId,
                    language: projectInuseDetail?.language || corporationDetail?.language || 'ja',
                }
            );

            if (!resultData?.status) {
                const startError = new Error(resultData?.errorMsg || resultData?.message || 'Failed to start App Integration session');
                startError.errorCode = resultData?.errorCode;
                throw startError;
            }

            const sessionData = normalizeAppIntegrationStartData(resultData, sessionId, startTime);
            completeAppIntegrationSession(sessionData);
        } catch (error) {
            console.error('startAppIntegrationCall error >>>', error);
            if (ENABLE_APP_INTEGRATION_FAKE_START) {
                console.warn('Using fake App Integration session for UI check. Remove ENABLE_APP_INTEGRATION_FAKE_START after backend is ready.');
                completeAppIntegrationSession(buildFakeAppIntegrationStartData(sessionId, startTime));
                return;
            }
            setAppIntegrationSession((currentSession) => (
                currentSession?.sessionId === sessionId ? null : currentSession
            ));
            setVoiceStatus(previousVoiceStatus || Constant.PHONE_AVAILABILITY.READY);
            updateAppIntegrationSelectionError(
                error?.errorCode === 'APP_INTEGRATION_SESSION_BUSY'
                    ? 'APP_INTEGRATION_SESSION_BUSY'
                    : 'INTERNAL_ERROR'
            );
        } finally {
            setIsStartingAppIntegration(false);
        }
    }, [
        clearAppIntegrationSelectionError,
        appIntegrationSession,
        corporationDetail?.language,
        corporationId,
        customerAudioDeviceId,
        handleClose,
        isStartingAppIntegration,
        projectId,
        projectInuseDetail?.language,
        saveAppIntegrationDevices,
        setAppIntegrationSession,
        setAppIntegrationSilence,
        updateAppIntegrationSelectionError,
        setVoiceStatus,
        userAudioDeviceId,
        userId,
        voiceStatus,
    ]);

    // Determine data source (search results vs default tab data)
    const currentData = hasSearched ? searchResults : tabData;

    // Pagination logic
    // TAB_TRAINING: backend pagination (all items in tabData are visible, "Load More" fetches next page)
    // TAB_HISTORY/CONTACTS: frontend pagination (slice from full array)
    const currentVisibleCount = activeTab === PHONE_SEARCH_TAB.HISTORY
        ? visibleHistoryCount
        : activeTab === PHONE_SEARCH_TAB.CONTACTS
            ? visibleContactCount
            : currentData.length; // TRAINING: show all loaded items

    const visibleItems = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? currentData
        : currentData.slice(0, currentVisibleCount);

    const displayTotalCount = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? totalCount
        : currentData.length;

    const hasMore = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? hasMoreTraining
        : currentVisibleCount < currentData.length;

    const handleLoadMore = () => {
        if (activeTab === PHONE_SEARCH_TAB.HISTORY) {
            setVisibleHistoryCount((prev) => prev + PHONE_SEARCH_PAGE_SIZE);
        } else {
            // TAB_CONTACTS & TAB_TRAINING: backend pagination
            loadMorePaginated();
        }
    };

    const isLoading = isLoadingTabData;

    // Check if direct call should be shown (no results, valid phone, on History or Contacts tab)
    const showDirectCall = !isLoading && !isSearching &&
        (Utils.isPublicDesk(projectInuseDetail) && Utils.isEnableExternalPhoneCall(corporationDetail, projectInuseDetail)) &&
        visibleItems.length === 0 &&
        Utils.isValidPhoneNumber(searchKeyword);

    const showNoResults = !isLoading && !isSearching && visibleItems.length === 0 && !showDirectCall;
    const isAppIntegrationDeviceSelectDisabled = isLoadingAppIntegrationDevices ||
        !isAppIntegrationMicPermissionGranted ||
        appIntegrationDevices.length === 0;
    const isAppIntegrationSelectionComplete = Boolean(customerAudioDeviceId && userAudioDeviceId);
    const isAppIntegrationSameDevice = isAppIntegrationSelectionComplete &&
        customerAudioDeviceId === userAudioDeviceId;
    const isAppIntegrationSessionBusy = Boolean(appIntegrationSession) ||
        [Constant.PHONE_AVAILABILITY.CONNECTING, Constant.PHONE_AVAILABILITY.ACTIVE].includes(voiceStatus);
    const isAppIntegrationStartDisabled = isStartingAppIntegration ||
        isAppIntegrationSessionBusy ||
        isAppIntegrationDeviceSelectDisabled ||
        !isAppIntegrationSelectionComplete ||
        isAppIntegrationSameDevice;
    const appIntegrationDisplayedError = appIntegrationDeviceError || appIntegrationSelectionError;
    const renderAppIntegrationSelectedDeviceValue = (selectedDeviceId) => {
        const selectedDevice = appIntegrationDevices.find((device) => device.deviceId === selectedDeviceId);
        if (selectedDevice) return selectedDevice.label;
        if (appIntegrationDevices.length === 0) return trI18n('APP_INTEGRATION_NO_DEVICE');
        return trI18n('APP_INTEGRATION_SELECT_DEVICE_PLACEHOLDER');
    };
    const renderAppIntegrationDeviceMenuItems = () => {
        if (appIntegrationDevices.length === 0) return (
            <MenuItem value="" disabled>
                {trI18n('APP_INTEGRATION_NO_DEVICE')}
            </MenuItem>
        );

        return appIntegrationDevices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.label}
            </MenuItem>
        ));
    };

    return (
        <>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                className="phone-search-popover-v2"
            >
                <div className="psv2-content">
                    {/* Tabs */}
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        className={`psv2-tabs${TAB_ORDER.length >= 4 ? ' psv2-tabs-compact' : ''}`}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        {isShowTabContactAndHistory &&
                            <Tab label={trI18n('TAB_HISTORY')} className="psv2-tab" />
                        }
                        {isShowTabContactAndHistory &&
                            <Tab label={trI18n('TAB_CONTACTS')} className="psv2-tab" />
                        }
                        {isShowTabTraining &&
                            <Tab label={trI18n('TAB_TRAINING')} className="psv2-tab" />
                        }
                        {isShowTabAppIntegration &&
                            <Tab label={trI18n('TAB_APP_INTEGRATION')} className="psv2-tab" />
                        }
                    </Tabs>

                    {/* Search Input - Hidden for App Integration tab */}
                    {activeTab !== PHONE_SEARCH_TAB.APP_INTEGRATION && (
                    <div className="psv2-search-wrapper">
                        {activeTab === PHONE_SEARCH_TAB.CONTACTS ? (
                            <div className="psv2-search-group">
                                <Select
                                    value={recipientType}
                                    onChange={handleRecipientTypeChange}
                                    variant="outlined"
                                    className="psv2-recipient-select"
                                    MenuProps={{
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        getContentAnchorEl: null
                                    }}
                                >
                                    <MenuItem value={RECIPIENT_TYPE.PROJECT_IN_ORG}>{trI18n('RECIPIENT_PROJECT_IN_ORG')}</MenuItem>
                                    {projectInuseDetail?.isDesk &&
                                        <MenuItem value={RECIPIENT_TYPE.USER_IN_ORG}>{trI18n('RECIPIENT_USER_IN_ORG')}</MenuItem>
                                    }
                                    {(Utils.isPublicDesk(projectInuseDetail) && Utils.isEnableExternalPhoneCall(corporationDetail, projectInuseDetail)) &&
                                        <MenuItem value={RECIPIENT_TYPE.B2C_CUSTOMER}>{trI18n('RECIPIENT_PHONE_CALL')}</MenuItem>
                                    }
                                </Select>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    variant="outlined"
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    size="small"
                                    className="psv2-search-field"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconButton size="small" onClick={performSearch} disabled={isSearching || !searchKeyword}>
                                                    <SearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        endAdornment: isSearching && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        ) : (
                            <TextField
                                autoFocus
                                fullWidth
                                variant="outlined"
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                onKeyPress={handleKeyPress}
                                size="small"
                                className="psv2-search-standalone"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton size="small" onClick={performSearch} disabled={isSearching || !searchKeyword}>
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: isSearching && (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </div>
                    )}

                    {/* Count Display */}
                    {activeTab !== PHONE_SEARCH_TAB.APP_INTEGRATION && !isLoading && !isSearching && displayTotalCount > 0 && (
                        <div className="psv2-count">
                            {trI18n('DISPLAY_COUNT')}: {visibleItems.length} / {displayTotalCount}
                        </div>
                    )}

                    {/* List Area - Hidden for App Integration tab */}
                    {activeTab !== PHONE_SEARCH_TAB.APP_INTEGRATION && (
                    <div className="psv2-list-area">
                        {isLoading && (
                            <div className="psv2-loading">
                                <CircularProgress size={24} />
                                <span className="psv2-loading-text">{trI18n('SEARCH_LOADING')}</span>
                            </div>
                        )}

                        {!isLoading && !isSearching && visibleItems.length > 0 && (
                            <List className="psv2-list">
                                {activeTab === PHONE_SEARCH_TAB.HISTORY && visibleItems.map((contact, index) => {
                                    const rawDisplayText = contact?.displayText || '';
                                    const displayText = contact?.phone && Utils.isPhoneLikeString(rawDisplayText)
                                        ? Utils.addPlusToPhoneNumber(rawDisplayText.trim())
                                        : rawDisplayText;
                                    const phoneData = {
                                        phone: contact.phone,
                                        name: displayText,
                                        type: contact.type,
                                        userId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.USER ? contact.id : null,
                                        projectId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.PROJECT ? contact.id : (contact.projectId || null),
                                        agentIdentity: contact.agentIdentity || null,
                                        callType: contact.type
                                    };
                                    return (
                                        <OverflowTooltip key={index} title={displayText}>
                                            <ListItem button className="psv2-list-item" onClick={() => {
                                                if (Boolean(contact?.phone)) return handleSelectPhone(phoneData)
                                                if (Boolean(contact?.voice)) return handleVoiceCall(phoneData)
                                                return null;
                                            }}>
                                                <div className="psv2-call-icons">
                                                    {Boolean(contact?.phone) && (
                                                        <div className={`psv2-icon-btn ${contact?.phone && 'bg-yellow'}`}>
                                                            <PhoneCallIcon />
                                                        </div>
                                                    )}
                                                    {Boolean(contact?.voice) && (
                                                        <div className={`psv2-icon-btn ${contact?.voice && 'bg-pink'}`}>
                                                            <VoiceCallIcon />
                                                        </div>
                                                    )}
                                                </div>
                                                <ListItemText primary={displayText} className="psv2-list-text" />
                                            </ListItem>
                                        </OverflowTooltip>
                                    );
                                })}

                                {activeTab === PHONE_SEARCH_TAB.CONTACTS && visibleItems.map((contact, index) => {
                                    const displayText = contact?.displayText || ''
                                    const phoneData = {
                                        phone: contact.phone,
                                        name: displayText,
                                        type: contact.type,
                                        userId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.USER ? contact.id : null,
                                        projectId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.PROJECT ? contact.id : (contact.projectId || null),
                                        agentIdentity: contact.agentIdentity || null,
                                        callType: contact.type
                                    };
                                    return (
                                        <OverflowTooltip key={index} title={displayText}>
                                            <ListItem button className="psv2-list-item" onClick={() => {
                                                if (Boolean(contact?.phone)) return handleSelectPhone(phoneData)
                                                if (Boolean(contact?.voice)) return handleVoiceCall(phoneData)
                                                return null;
                                            }}>
                                                <div className="psv2-call-icons">
                                                    {Boolean(contact?.phone) && (
                                                        <div className={`psv2-icon-btn ${contact?.phone && 'bg-yellow'}`}>
                                                            <PhoneCallIcon />
                                                        </div>
                                                    )}
                                                    {Boolean(contact?.voice) && (
                                                        <div className={`psv2-icon-btn ${contact?.voice && 'bg-pink'}`}>
                                                            <VoiceCallIcon />
                                                        </div>
                                                    )}
                                                </div>
                                                <ListItemText primary={displayText} className="psv2-list-text" />
                                            </ListItem>
                                        </OverflowTooltip>
                                    );
                                })}

                                {activeTab === PHONE_SEARCH_TAB.TRAINING && visibleItems.map((ticket) => (
                                    <OverflowTooltip key={ticket?.id} title={tr(ticket?.outline)}>
                                        <ListItem button onClick={() => handleMakeCallAiTraining(ticket)} className="psv2-list-item">
                                            <ListItemText primary={tr(ticket?.outline)} className="psv2-list-text" />
                                        </ListItem>
                                    </OverflowTooltip>
                                ))}
                            </List>
                        )}

                        {/* Direct Call Button */}
                        {showDirectCall && (
                            <div className="psv2-direct-call">
                                {(() => {
                                    const matchedB2CUser = findB2CUserByPhone(searchKeyword);
                                    const displayLabel = matchedB2CUser?.fullName
                                        ? `${matchedB2CUser.fullName} (${Utils.addPlusToPhoneNumber(Utils.cleanPhone(searchKeyword))})`
                                        : Utils.addPlusToPhoneNumber(Utils.cleanPhone(searchKeyword));
                                    const callToText = trI18n('CALL_TO');
                                    const fullTitle = `${displayLabel}${callToText}`;
                                    return (
                                        <Tooltip title={fullTitle} placement="top">
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                className="btn-direct-call"
                                                startIcon={!Utils.isEmptyObject(matchedB2CUser) ? <></> : <AddCircleIcon />}
                                                onClick={handleDirectCall}
                                            >
                                                <span className="psv2-direct-call-text">{displayLabel}{callToText}</span>
                                            </Button>
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                        )}

                        {/* No Results */}
                        {showNoResults && (
                            <div className="psv2-no-results">
                                {trI18n('SEARCH_EMPTY_RESULT')}
                            </div>
                        )}

                        {/* Load More */}
                        {hasMore && !isLoading && !isSearching && (
                            <div className="psv2-load-more">
                                {isLoadingMore ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <button
                                        onClick={handleLoadMore}
                                        className="btn-reset-default psv2-load-more-link"
                                    >
                                        {trI18n('SEE_MORE')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    )}

                    {/* App Integration Tab Content */}
                    {activeTab === PHONE_SEARCH_TAB.APP_INTEGRATION && (
                        <div className="psv2-app-integration">
                            <div className="psv2-app-integration-title">
                                {trI18n('APP_INTEGRATION_AUDIO_DEVICE_SETTING')}
                            </div>

                            <div className="psv2-app-integration-form">
                                {/* Customer Audio Device */}
                                <div className="psv2-app-integration-field">
                                    <label className="psv2-app-integration-label">
                                        {trI18n('APP_INTEGRATION_CUSTOMER_AUDIO_DEVICE')}
                                    </label>
                                    <Select
                                        fullWidth
                                        variant="outlined"
                                        value={customerAudioDeviceId}
                                        onChange={(event) => handleChangeCustomerAudioDevice(event.target.value)}
                                        disabled={isAppIntegrationDeviceSelectDisabled}
                                        displayEmpty
                                        renderValue={renderAppIntegrationSelectedDeviceValue}
                                        className="psv2-app-integration-select"
                                        MenuProps={{
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                                            getContentAnchorEl: null,
                                            classes: { list: 'psv2-app-integration-menu' }
                                        }}
                                    >
                                        {renderAppIntegrationDeviceMenuItems()}
                                    </Select>
                                </div>

                                {/* User Audio Device */}
                                <div className="psv2-app-integration-field">
                                    <label className="psv2-app-integration-label">
                                        {trI18n('APP_INTEGRATION_USER_AUDIO_DEVICE')}
                                    </label>
                                    <Select
                                        fullWidth
                                        variant="outlined"
                                        value={userAudioDeviceId}
                                        onChange={(event) => handleChangeUserAudioDevice(event.target.value)}
                                        disabled={isAppIntegrationDeviceSelectDisabled}
                                        displayEmpty
                                        renderValue={renderAppIntegrationSelectedDeviceValue}
                                        className="psv2-app-integration-select"
                                        MenuProps={{
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                                            getContentAnchorEl: null,
                                            classes: { list: 'psv2-app-integration-menu' }
                                        }}
                                    >
                                        {renderAppIntegrationDeviceMenuItems()}
                                    </Select>
                                </div>

                                {/* Error Area - placeholder for ST-02/ST-03 */}
                                <div className="psv2-app-integration-error">
                                    {appIntegrationDisplayedError}
                                </div>

                                {/* Start Button */}
                                <div className="psv2-app-integration-action">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className="psv2-app-integration-start-btn"
                                        disabled={isAppIntegrationStartDisabled}
                                        onClick={handleStartAppIntegration}
                                    >
                                        {trI18n('APP_INTEGRATION_START')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Popover>

            <DataChangePleaseReloadModal
                isOpen={showErrorModal}
                setIsOpen={setShowErrorModal}
            />
        </>
    );
}

export default PhoneSearchPopoverV2;
